/**
 * Per-(layer × color × linetype) BatchedMesh wrapper.
 *
 * # Why this layer of indirection
 *
 * Three.js `LineSegments2` (fat-line addon) draws all of its segments
 * with a single material. To honour BYLAYER colour + linetype + width
 * we need one mesh per *visual class* — i.e. one mesh per unique
 * (layer name, color hex, linetype name) triple. `batchedLayer.ts`
 * owns the bookkeeping: which entity's vertices live in which mesh,
 * and how many segments each mesh holds.
 *
 * # Why Three.js types are not imported here
 *
 * `LineSegments2` lives under `three/examples/jsm/lines/LineSegments2`
 * which pulls in shader code and a GL context. We must NOT import it
 * at module load — jsdom unit tests for adapters import `batchedLayer`
 * directly, and the test runner has no GL context. Instead the wrapper
 * is generic over a `Mesh<TGeometry>` shape and accepts a factory
 * function the caller provides at runtime. Production callers wire up
 * the factory to `new LineSegments2()`; tests stub it with a no-op
 * mesh.
 *
 * # Slot bookkeeping (Q18.B prep)
 *
 * `entityIdToSlot` records, for each entity id:
 *   - `meshKey`     — the (layer × color × linetype) bucket it lives in
 *   - `vertexStart` — offset into the bucket's flat vertex buffer
 *   - `vertexCount` — number of vertices the entity contributes
 *
 * That's enough to support `removeEntity(id)` (free the slot) and
 * `updateEntity(id, next)` (rewrite the slot in place). The actual
 * per-instance state DataTexture (selection / hover / visibility) is
 * deferred to PR-C — see TODO marker below.
 *
 * # Layer cap (Q22 hot-path)
 *
 * A single mesh exceeding 200 000 segments is split into a follow-up
 * mesh with the same key. The cap prevents an O(N²) re-allocation when
 * one layer dominates a 685k-entity drawing.
 */

import type { RenderableEntity, SceneGeometry, Vec3 } from "./types";
import { bucketParentLayers } from "./layerChainVisibility";
import { tessellateEllipse } from "./adapters/arcEllipseSpline";
import { sampleCatmullRomCubic } from "./adapters/geom/catmullRom";
import {
  BAND_ARC_SAMPLES_PER_TURN,
  polylineCentrelineSamples,
} from "./adapters/geom/polylineWidthBand";
import {
  drawnPolylineBandLoops,
  fillModePaintsSolid,
  type DrawnBandGeometry,
} from "./adapters/geom/polylineDrawnBand";
import { withPolylineThicknessSweep } from "./adapters/geom/polylineThicknessSweep";
import {
  clear as clearTessellation,
  getOrTessellate,
  invalidate as invalidateTessellation,
} from "./adapters/tessellationCache";
import {
  compareDrawOrder,
  planDrawOrderStrata,
  SINGLE_DRAW_ORDER_STRATUM,
  type DrawOrderStrata,
} from "./drawOrderStrata";

/**
 * Maximum number of *segments* any single mesh holds. Layers with more
 * geometry are split across multiple meshes that share the same
 * meshKey prefix.
 */
const DEFAULT_SEGMENT_CAP = 200_000;

/** Composite key uniquely identifying one mesh bucket. */
export interface MeshKey {
  layer: string;
  colorHex: string;
  linetype: string;
  /**
   * Codex PR #784 round 3 P1 — the enclosing block-reference layers of the
   * entities in this bucket that DIFFER from `layer` (`bucketParentLayers`:
   * deduplicated, sorted). A block child on layer B under an INSERT on layer
   * A buckets as `(B, parents=[A])`, separate from B's top-level bucket, so
   * hiding A (LAYER off / freeze, VPLAYER) is a per-bucket toggle exactly
   * like hiding B — no per-slot zero-fill. Absent (not `[]`) for a
   * top-level entity or a child whose chain only repeats its own layer, so
   * those keep the pre-existing `(layer, colour, linetype)` key verbatim.
   */
  parentLayers?: readonly string[];
  /**
   * The entity's DRAWORDER stratum (`drawOrderStrata.ts`). Present only when
   * NON-ZERO, so a drawing that needs no cut — every drawing with no wide
   * polyline, and every drawing whose bands overlap nothing authored before
   * them — keeps the pre-existing key and the pre-existing `renderOrder` 0
   * verbatim.
   *
   * It is part of the key because the bucket is the unit of `renderOrder`:
   * a direct band-fill mesh can only be ordered BETWEEN two groups of line
   * work if the line work on either side of it is in different buckets. See
   * `drawOrderStrata.ts` for why the cut is made as rarely as it is.
   */
  drawOrderStratum?: number;
  /**
   * Codex round 15 P1 (`scene/cacheDirectSpaces.ts:63`) — the PAPER space the
   * entities of this bucket live in. Absent (not `""`) for model space, so a
   * model-only drawing — and every pre-existing key — stays byte-identical,
   * exactly like `parentLayers` and `drawOrderStratum` above.
   *
   * It is part of the key because a bulk-load pass on a SHEET renders both the
   * layout and model space (`scene/cacheDirectSpaces.ts resolveRenderSpaceIds`
   * returns `[layout, "model"]`, deliberately: the per-viewport scissor passes
   * MASK geometry that already exists in the scene — they lift nothing). With
   * no space component a model line and a paper line that agree on layer ×
   * colour × linetype — layer "0", BYLAYER colour, Continuous: the commonest
   * shape in any drawing — coalesced into ONE `LineSegments2`.
   * `runSceneBulkLoad` then classified that bucket `"mixed"`, which
   * `rendering/viewportTraversalCaches.ts` deliberately KEEPS in the bare-sheet
   * pass (it must not hide the paper half), so the model half painted directly
   * on the sheet, outside every viewport scissor rect. The viewport pass could
   * not suppress the paper half independently either — one mesh carries one
   * visibility flag.
   *
   * The FULL space id is stored rather than a model/paper bit so two layouts
   * can never alias, and so the value is THREADED DOWN from
   * `RenderableEntity.spaceId` (the pass's existing space truth) instead of
   * being re-derived inside the renderer cache.
   */
  space?: string;
}

/** Stringified MeshKey used as a `Map<string, Bucket>` lookup key. */
export type MeshKeyId = string;

export function meshKeyToId(key: MeshKey): MeshKeyId {
  // No real risk of collision — `layer` and `linetype` are user-typed
  // names that may contain '|', so we URL-encode the field separator.
  const base = `${encodeURIComponent(key.layer)}|${key.colorHex}|${encodeURIComponent(
    key.linetype,
  )}`;
  // The chain is appended AFTER the three fixed fields so
  // `extractLayerFromKeyId` (first field) keeps working; each name is
  // encoded so a layer containing '|' or the '#' split suffix cannot alias.
  const chained =
    !key.parentLayers || key.parentLayers.length === 0
      ? base
      : `${base}|${key.parentLayers.map(encodeURIComponent).join("|")}`;
  // Codex round 15 P1 — the paper space rides behind a literal `#`, which
  // `encodeURIComponent` escapes to `%23` in every user-typed field before it,
  // so no layer or linetype name can spell this suffix. Omitted for model
  // space, which keeps every pre-existing key byte-identical.
  const spaced = key.space
    ? `${chained}|#p${encodeURIComponent(key.space)}`
    : chained;
  // The stratum rides LAST, behind the same `#` guard. Omitted for stratum 0,
  // which keeps every pre-existing key byte-identical.
  if (!key.drawOrderStratum) return spaced;
  return `${spaced}|#s${key.drawOrderStratum}`;
}

/**
 * The ONE derivation of an entity's bucket key (insert / patch / re-bucket
 * all route through it): layer × colour × linetype, plus the block-reference
 * layer chain when it differs from the entity's own layer (see
 * `MeshKey.parentLayers`).
 *
 * PR-LT3 — lines honour their real linetype (was hardcoded "Continuous");
 * `extractLinetype` reads the name the adapter propagated onto
 * `RenderableEntity.linetype`.
 *
 * Codex round 15 P1 — plus the entity's PAPER space (`MeshKey.space`), so a
 * pass that renders a layout and model space together (the streamed-sheet
 * case) can never coalesce their geometry into one mesh.
 */
function bucketKeyFor(
  entity: RenderableEntity,
  drawOrderStratum: number,
): MeshKey {
  const key: MeshKey = {
    layer: entity.layer,
    colorHex: entity.colorHex,
    linetype: extractLinetype(entity),
  };
  const parentLayers = bucketParentLayers(entity);
  if (parentLayers) key.parentLayers = parentLayers;
  if (drawOrderStratum > 0) key.drawOrderStratum = drawOrderStratum;
  const space = bucketSpaceTag(entity.spaceId);
  if (space !== undefined) key.space = space;
  return key;
}

/**
 * Codex round 15 P1 — the ONE normalisation of a renderable's `spaceId` into
 * the bucket key's optional `space` component: the id itself for a PAPER space
 * (any non-`"model"` layout block name), `undefined` for model space.
 *
 * `spaceIsPaperFlag` (the per-vertex attribute below) routes through it too,
 * so the bucket partition and the per-segment global-LTSCALE selection can
 * never disagree about which space a segment belongs to.
 */
function bucketSpaceTag(spaceId: string | undefined): string | undefined {
  return spaceId !== undefined && spaceId !== "model" ? spaceId : undefined;
}

/**
 * Slot index into a single mesh bucket. Allows O(1) updateEntity /
 * removeEntity once the bucket layout is decided.
 */
export interface EntitySlot {
  meshKey: MeshKeyId;
  /** Index of the entity's first vertex inside the bucket's flat array. */
  vertexStart: number;
  /** Number of vertices the entity contributes. */
  vertexCount: number;
  /**
   * 0-based ordinal of the entity within its bucket — used by the
   * `selectionStateTexture` to index per-instance state bytes (Q18.B).
   * Stable across `getSlot` calls for the lifetime of the bucket; gets
   * recomputed on the next `bulkLoad`.
   */
  instanceIndex: number;
}

/**
 * One mesh bucket: a stable identity (`key`), the flat vertex stream,
 * and the running count of segments.
 *
 * Vertices are flattened triples: `[x0, y0, z0, x1, y1, z1, …]`. The
 * caller's mesh factory turns this into either a `LineSegments2`
 * geometry (for line / arc / polyline kinds) or a `Mesh` (for tapered
 * polyline / dimension primitives). The flat layout matches Three's
 * `BufferAttribute` constructor directly.
 */
export interface MeshBucket {
  key: MeshKey;
  keyId: MeshKeyId;
  /**
   * Flat vertex stream: 3 floats per vertex. Codex P4 — backed by a
   * Float32Array (4 bytes per element) instead of JS `number[]`
   * (8 bytes per element via V8 PACKED_DOUBLE). Capacity may exceed
   * the populated range; `vertexLength` tracks how many floats are
   * actually written. Direct indexed reads/writes (`[i] = 0` for
   * zero-fill on `removeEntity`) still work because typed arrays
   * support the same accessor pattern.
   */
  vertices: Float32Array;
  /** Count of populated floats in `vertices` (`<= vertices.length`). */
  vertexLength: number;
  /** Number of segments (= vertexLength / 6 for line-pair format). */
  segmentCount: number;
  /** True once this bucket reached its segment cap and was split. */
  splitFromKeyId?: MeshKeyId;
  /**
   * Per-vertex instance index (Q18.B). Each vertex carries the bucket-
   * local instance ordinal of the entity it belongs to, so the vertex
   * shader can sample the selection-state DataTexture by that index.
   *
   * Codex P4 — Float32Array storage to mirror the `vertices` layout
   * (half the memory of `number[]`). `instanceSlotLength` is the
   * populated count, mirroring `vertexLength` for `vertices`.
   * Population matches `entityToSlot[id].instanceIndex` for every
   * vertex contributed by entity `id`.
   */
  instanceSlotIndices: Float32Array;
  /** Count of populated entries in `instanceSlotIndices`. */
  instanceSlotLength: number;
  /**
   * PR-LT3 variant A (A2) — per-vertex per-entity linetype-scale RECIPROCAL
   * (1 / CELTSCALE, DXF group 48). Mirrors `instanceSlotIndices` exactly: one
   * float per vertex, populated from the entity's `RenderableEntity.linetypeScale`
   * for every vertex it contributes. The vertex shader multiplies
   * `vLineDistance` by this (under `USE_INSTANCE_LTSCALE`) so a larger CELTSCALE
   * yields longer dashes WITHOUT changing the bucket key (CELTSCALE is NOT part
   * of the MeshKey — adding it would explode buckets). A `0` / absent scale
   * defaults to 1 (reciprocal 1 → no scaling). `instanceLtScaleLength` is the
   * populated count, mirroring `instanceSlotLength`.
   */
  instanceLtScales: Float32Array;
  /** Count of populated entries in `instanceLtScales`. */
  instanceLtScaleLength: number;
  /**
   * PR-LT3 variant A (A2) — FIX (Codex P2, mixed-space bucket). Per-vertex
   * model/paper flag (`1` = paper-space entity, `0` = model-space) mirroring
   * `instanceLtScales` exactly: one float per vertex. The GLOBAL linetype scale
   * (LTSCALE × MSLTSCALE|PSLTSCALE) is folded into the per-segment
   * `instanceLtScale` at bucket-build, and it differs by space — so the consumer
   * needs to know EACH segment's space.
   *
   * Codex round 15 P1 — `MeshKey.space` now partitions buckets by space, so
   * every segment of a bucket carries the SAME flag; the attribute is retained
   * (not collapsed to a per-bucket uniform) because the fold site
   * `rendering/perSegmentLtScale.ts` is also the RELIFT site, where segments
   * are appended to a live bucket one entity at a time and a uniform would have
   * to be re-derived from the bucket key on every append. Same cardinality /
   * fill sites as `instanceLtScales`; `instanceSpaceIsPaperLength` is the
   * populated count.
   */
  instanceSpaceIsPaper: Float32Array;
  /** Count of populated entries in `instanceSpaceIsPaper`. */
  instanceSpaceIsPaperLength: number;
  /**
   * Number of distinct entities living in this bucket — equals the
   * highest `instanceIndex + 1` seen during inserts. Used as the
   * `capacity` argument for the per-bucket selection state texture.
   */
  instanceCount: number;
}

/**
 * PR-LT3 variant A (A2) — convert a renderable's per-entity linetype scale
 * (CELTSCALE, DXF group 48) into the RECIPROCAL the vertex shader consumes.
 * A larger CELTSCALE must enlarge dashes; the fragment walks
 * `mod(vLineDistance, period)` over a FIXED (un-scaled) period, so the shader
 * scales `vLineDistance` DOWN by `1 / CELTSCALE` (fewer periods over the same
 * geometry length → longer dashes). A non-positive / undefined scale → 1.
 */
function ltScaleReciprocal(scale: number | undefined): number {
  return scale !== undefined && scale > 0 ? 1 / scale : 1;
}

/**
 * PR-LT3 variant A (A2). Per-vertex model/paper flag stored in
 * `instanceSpaceIsPaper`: `1` for a paper-space entity (any non-`"model"`
 * spaceId — a layout block name), `0` for model space. The bucket-build /
 * relift per-segment fold reads this to select MSLTSCALE (model) vs the
 * PSLTSCALE-flag (paper) for the GLOBAL scale folded into each segment's
 * `instanceLtScale`. Shares `bucketSpaceTag` with the bucket key, so the
 * partition and the scale selection cannot disagree.
 */
function spaceIsPaperFlag(spaceId: string | undefined): number {
  return bucketSpaceTag(spaceId) !== undefined ? 1 : 0;
}

/**
 * Codex P4 — initial capacity for a freshly-acquired bucket's typed
 * arrays. Picked to cover ~32 small entities without re-growth (e.g.
 * 192 floats = 32 LINE segments × 6 floats); doubles on demand.
 */
const BUCKET_INITIAL_FLOAT_CAPACITY = 192;

/**
 * Codex P4 — grow a bucket's typed arrays so they can accept the
 * requested additional float counts. Doubles existing capacity (or
 * jumps to the required size if double is still too small). New
 * allocations zero-fill automatically, so callers can rely on
 * `arr[i] === 0` for any index beyond `*Length` even before they
 * write to it.
 */
function ensureBucketCapacity(
  bucket: MeshBucket,
  addedVertexFloats: number,
  addedInstanceFloats: number,
): void {
  const needV = bucket.vertexLength + addedVertexFloats;
  if (needV > bucket.vertices.length) {
    let cap = Math.max(bucket.vertices.length, 1);
    while (cap < needV) cap *= 2;
    const grown = new Float32Array(cap);
    grown.set(bucket.vertices);
    bucket.vertices = grown;
  }
  const needI = bucket.instanceSlotLength + addedInstanceFloats;
  if (needI > bucket.instanceSlotIndices.length) {
    let cap = Math.max(bucket.instanceSlotIndices.length, 1);
    while (cap < needI) cap *= 2;
    const grown = new Float32Array(cap);
    grown.set(bucket.instanceSlotIndices);
    bucket.instanceSlotIndices = grown;
  }
  // PR-LT3 variant A (A2) — grow the parallel per-vertex CELTSCALE-reciprocal
  // buffer in lockstep with `instanceSlotIndices` (same per-vertex cardinality).
  const needL = bucket.instanceLtScaleLength + addedInstanceFloats;
  if (needL > bucket.instanceLtScales.length) {
    let cap = Math.max(bucket.instanceLtScales.length, 1);
    while (cap < needL) cap *= 2;
    const grown = new Float32Array(cap);
    grown.set(bucket.instanceLtScales);
    bucket.instanceLtScales = grown;
  }
  // PR-LT3 variant A (A2) — grow the parallel per-vertex space flag buffer in
  // lockstep with `instanceLtScales` (same per-vertex cardinality).
  const needS = bucket.instanceSpaceIsPaperLength + addedInstanceFloats;
  if (needS > bucket.instanceSpaceIsPaper.length) {
    let cap = Math.max(bucket.instanceSpaceIsPaper.length, 1);
    while (cap < needS) cap *= 2;
    const grown = new Float32Array(cap);
    grown.set(bucket.instanceSpaceIsPaper);
    bucket.instanceSpaceIsPaper = grown;
  }
}

/**
 * Public API of the per-layer batch manager. Implementation below; tests
 * import the class directly, production callers go through this type.
 */
export interface BatchedLayerApi {
  /**
   * @param plan the DRAWORDER strata plan to use instead of cutting one from
   *   this feed — the cache-direct branch cuts it from the WHOLE drawing
   *   because its feed is only what survived the LOD pre-classify
   *   (`scene/drawOrderPlanForDrawing.ts`).
   */
  bulkLoad(entities: RenderableEntity[], plan?: DrawOrderStrata): void;
  /**
   * The DRAWORDER strata the last `bulkLoad` planned (or was given). The
   * bucket meshes and
   * every direct fill mesh read their `renderOrder` off it, so both sides of
   * the painter sequence come from ONE plan (`drawOrderStrata.ts`).
   */
  getDrawOrderStrata(): DrawOrderStrata;
  addEntity(id: string, entity: RenderableEntity): void;
  removeEntity(id: string): void;
  updateEntity(id: string, next: RenderableEntity): void;
  /**
   * Phase 2 Wave 2 Task 2 — patch an entity in place if possible.
   * - If the existing slot's bucket still matches the new entity's
   *   bucket key AND the new tessellation produces the same vertex
   *   count, overwrite the existing slot's vertex range in place.
   *   Returns `"patched"`.
   * - Otherwise falls back to remove+add (slow path). Returns
   *   `"reassigned"` if the bucket changed, `"resized"` if the vertex
   *   count differs.
   */
  patchEntity(id: string, next: RenderableEntity): BatchedLayerPatchResult;
  /**
   * Phase 2 Wave 2 Task 2 — re-assign an entity from one bucket to
   * another without re-tessellating vertices. Cheap path for
   * style-only changes (layer / color / linetype) where the existing
   * slot's vertex data is still valid but the bucket key changed.
   * Copies the existing slot's vertex range into the new bucket and
   * zero-fills the old slot. No-op when the bucket key is unchanged.
   */
  updateEntityBucket(
    id: string,
    next: RenderableEntity,
  ): BatchedLayerPatchResult;
  getBuckets(): ReadonlyArray<MeshBucket>;
  getSlot(id: string): EntitySlot | undefined;
  size(): number;
  clear(): void;
}

/**
 * Outcome of a patch/re-bucket call. The renderer reads this to decide
 * whether the bucket's GPU buffer needs a full re-upload (`"resized"`),
 * a partial sub-range upload (`"patched"`), or a re-attachment of the
 * mesh to a new bucket (`"reassigned"` / `"rebucketed"`).
 */
export type BatchedLayerPatchResult =
  | "patched"
  | "resized"
  | "reassigned"
  | "rebucketed"
  | "noop"
  | "absent";

/**
 * Default layer/colour/linetype batch manager.
 *
 * The class is deliberately renderer-agnostic — production callers
 * subscribe to `getBuckets()` and convert each bucket into a
 * `LineSegments2` mesh on the GPU side. Tests assert on the bucket
 * geometry (vertex coords, slot offsets) directly without touching
 * Three.js.
 */
/**
 * Phase 2 Wave 2 Task 3 — resolver that looks up an entity's current
 * `geomRev` (geometry revision counter) on demand. Plug in to enable
 * the per-entity tessellation cache; omit to run uncached (every
 * insert re-tessellates).
 *
 * Source of truth in production: `EntityMetadataIndex.get(id).geomRev`.
 * Tests can pass a Map-backed stub. The resolver MUST be cheap (O(1))
 * — it is invoked per entity on every bulkLoad.
 */
export type GeomVersionResolver = (entityId: string) => number;

export class BatchedLayer implements BatchedLayerApi {
  private readonly buckets: Map<MeshKeyId, MeshBucket>;
  private readonly entityToSlot: Map<string, EntitySlot>;
  private readonly segmentCap: number;
  private readonly resolveGeomVersion: GeomVersionResolver | undefined;
  /**
   * The DRAWORDER strata plan for the CURRENT contents. Replaced on every
   * `bulkLoad`, reset by `clear()`; `SINGLE_DRAW_ORDER_STRATUM` (stratum 0 for
   * everything) until a bulk load plans otherwise, so a layer populated purely
   * through `addEntity` behaves exactly as it did before strata existed.
   */
  private strata: DrawOrderStrata = SINGLE_DRAW_ORDER_STRATUM;

  /**
   * @param segmentCap — soft cap for a single bucket's segment count
   *   before a split into a `#N` follow-up bucket happens.
   * @param resolveGeomVersion — (Wave 2) optional resolver invoked per
   *   entity insertion to retrieve the entity's geomRev. When supplied,
   *   `insertEntityVertices` and `patchEntity` route the per-entity
   *   tessellation through `tessellationCache.getOrTessellate`. When
   *   omitted, every insert re-tessellates from scratch (legacy
   *   behaviour — preserved for existing tests that construct a
   *   BatchedLayer without wiring the cache).
   */
  constructor(
    segmentCap = DEFAULT_SEGMENT_CAP,
    resolveGeomVersion?: GeomVersionResolver,
  ) {
    this.buckets = new Map();
    this.entityToSlot = new Map();
    this.segmentCap = segmentCap;
    this.resolveGeomVersion = resolveGeomVersion;
  }

  /**
   * Bulk-populate from an entity list. Sorts by `zOrder` ascending,
   * ties broken by entity id, before packing into buckets — this is
   * the DRAWORDER preservation step required by the spec.
   *
   * `bulkLoad` clears any prior state.
   */
  bulkLoad(entities: RenderableEntity[], plan?: DrawOrderStrata): void {
    this.clear();
    const sorted = [...entities].sort(compareDrawOrder);
    // The strata plan is cut from the SAME sorted feed that packs the buckets,
    // so a bucket's stratum and the order its segments were appended in are
    // two readings of one sequence (`drawOrderStrata.ts`).
    //
    // …UNLESS the caller supplies one. On the cache-direct branch this feed is
    // only what survived the LOD pre-classify, and the entities it dropped
    // arrive later through `addEntity` (`FrameLoops` Pass 2). A plan cut from
    // the survivors has no boundary where those entities are authored, so the
    // caller cuts it from the WHOLE drawing instead and hands it in here
    // (`scene/drawOrderPlanForDrawing.ts`). Either way the plan is the ONE the
    // direct fill meshes read (`getDrawOrderStrata`), so the two sides of the
    // painter sequence cannot drift.
    this.strata = plan ?? planDrawOrderStrata(sorted);
    for (const entity of sorted) {
      this.insertEntityVertices(entity);
    }
  }

  getDrawOrderStrata(): DrawOrderStrata {
    return this.strata;
  }

  /** Add a single entity. Maintains DRAWORDER append-only semantics. */
  addEntity(id: string, entity: RenderableEntity): void {
    if (this.entityToSlot.has(id)) this.removeEntity(id);
    this.insertEntityVertices(entity);
  }

  /**
   * Remove an entity. Vertices are NOT compacted — instead they are
   * zero-filled in place, leaving a hole. This matches Three.js's
   * `BatchedMesh` deletion strategy and keeps remove() at O(slot).
   *
   * On the next `bulkLoad`, holes are reclaimed automatically.
   */
  removeEntity(id: string): void {
    const slot = this.entityToSlot.get(id);
    if (!slot) return;
    const bucket = this.buckets.get(slot.meshKey);
    if (!bucket) {
      this.entityToSlot.delete(id);
      // Cache might still hold a stale buffer if construction wired a
      // resolver — drop it so the next add for the same id repopulates.
      if (this.resolveGeomVersion) invalidateTessellation(id);
      return;
    }
    // Zero-fill the slot to make the gap invisible without re-packing.
    for (
      let i = slot.vertexStart;
      i < slot.vertexStart + slot.vertexCount * 3;
      i++
    ) {
      bucket.vertices[i] = 0;
    }
    this.entityToSlot.delete(id);
    // Phase 2 Wave 2 Task 3 — drop the cached tessellation alongside the
    // slot. Without this, removed entities keep a Float32Array pinned in
    // the cache map until clear()/invalidate() runs explicitly.
    if (this.resolveGeomVersion) invalidateTessellation(id);
  }

  /**
   * Update an entity in place. Today: implemented as remove + add. A
   * future PR may patch the slot directly when the new vertex count
   * matches the old (saves one map lookup per edit).
   */
  updateEntity(id: string, next: RenderableEntity): void {
    this.removeEntity(id);
    this.addEntity(id, next);
  }

  /**
   * Phase 2 Wave 2 Task 2 — fast incremental update.
   *
   * Compares the new entity's bucket key + vertex count against the
   * existing slot. Three outcomes:
   *
   *   1. Same bucket, same vertex count → splice new vertices into the
   *      existing slot's range. Returns `"patched"`. The renderer can
   *      issue a `setPositions(...)` partial upload covering the slot's
   *      sub-range, but a full-buffer reupload is also legal.
   *
   *   2. Same bucket key, different vertex count → fall back to
   *      remove + add. Returns `"resized"`. We can't shrink/grow the
   *      slot without compacting; remove+add hands the entity a fresh
   *      slot at the bucket's tail.
   *
   *   3. Different bucket key (style change moved it to another
   *      layer/color/linetype) → fall back to remove + add. Returns
   *      `"reassigned"`. The old slot is zero-filled; the new slot
   *      lives in the matching bucket.
   *
   * Returns `"absent"` if `id` isn't in the layer.
   */
  patchEntity(id: string, next: RenderableEntity): BatchedLayerPatchResult {
    const slot = this.entityToSlot.get(id);
    if (!slot) {
      this.addEntity(id, next);
      // The caller asked for a patch, but the entity wasn't there —
      // we added it. Report `"absent"` so the caller knows the slot
      // didn't pre-exist.
      return "absent";
    }
    const verts = this.tessellateEntity(next);
    if (verts.length === 0) {
      // No vertex stream → remove the slot entirely.
      this.removeEntity(id);
      return "resized";
    }
    const newVertexCount = verts.length / 3;
    const newBucketKey = bucketKeyFor(next, this.strata.stratumOf(next));
    const newKeyId = meshKeyToId(newBucketKey);
    const existingBucket = this.buckets.get(slot.meshKey);
    if (!existingBucket) {
      // Bucket gone (shouldn't happen if entityToSlot is consistent) —
      // fall back to add.
      this.entityToSlot.delete(id);
      this.insertEntityVertices(next);
      return "reassigned";
    }
    // Identify the existing bucket's base key (drop the `#N` split
    // suffix) — splits share the same logical (layer × color × linetype).
    const existingBaseKey =
      existingBucket.splitFromKeyId ?? existingBucket.keyId;
    if (existingBaseKey !== newKeyId) {
      // Style change moved the entity to a different bucket. Slow path.
      this.removeEntity(id);
      this.insertEntityVertices(next);
      return "reassigned";
    }
    if (slot.vertexCount !== newVertexCount) {
      // Vertex count differs — can't reuse the slot.
      this.removeEntity(id);
      this.insertEntityVertices(next);
      return "resized";
    }
    // Fast path: same bucket, same vertex count → in-place patch.
    // Splice the new vertices into the existing flat-vertex range.
    for (let i = 0; i < verts.length; i++) {
      existingBucket.vertices[slot.vertexStart + i] = verts[i] ?? 0;
    }
    // instanceSlotIndices is per-vertex; same entity ordinal, same count,
    // so the existing entries are already correct — no rewrite needed.
    // PR-LT3 variant A (A2) — CELTSCALE is NOT part of the bucket key, so a
    // pure per-entity linetype-scale change reaches this fast path with the
    // same bucket + vertex count. Rewrite the slot's per-vertex reciprocals so
    // the new CELTSCALE takes effect without a full re-bucket. The instance
    // arrays are indexed per-vertex; the slot's instance offset is
    // `vertexStart / 3` (3 floats per vertex in `vertices`).
    const ltReciprocal = ltScaleReciprocal(next.linetypeScale);
    const isPaper = spaceIsPaperFlag(next.spaceId);
    const instanceStart = slot.vertexStart / 3;
    for (let i = 0; i < slot.vertexCount; i++) {
      existingBucket.instanceLtScales[instanceStart + i] = ltReciprocal;
      existingBucket.instanceSpaceIsPaper[instanceStart + i] = isPaper;
    }
    return "patched";
  }

  /**
   * Phase 2 Wave 2 Task 2 — re-bucket an entity on a pure style change.
   *
   * When the entity's geometry (and therefore tessellated vertex
   * stream) is unchanged but its `(layer, color, linetype)` key
   * differs from the slot's bucket, copy the EXISTING slot's vertex
   * range into a fresh slot in the new bucket and zero-fill the old.
   * Returns `"rebucketed"` on success, `"noop"` if the bucket key is
   * unchanged, `"absent"` if the id is not in the layer.
   *
   * This is the renderer's incremental path for style-only mutations
   * (`changedFields === "style"`) — no tessellation work, no vertex
   * recomputation, just a slot move.
   */
  updateEntityBucket(
    id: string,
    next: RenderableEntity,
  ): BatchedLayerPatchResult {
    const slot = this.entityToSlot.get(id);
    if (!slot) return "absent";
    const existingBucket = this.buckets.get(slot.meshKey);
    if (!existingBucket) {
      this.entityToSlot.delete(id);
      return "absent";
    }
    const newBucketKey = bucketKeyFor(next, this.strata.stratumOf(next));
    const newKeyId = meshKeyToId(newBucketKey);
    const existingBaseKey =
      existingBucket.splitFromKeyId ?? existingBucket.keyId;
    if (existingBaseKey === newKeyId) {
      // Bucket key unchanged — nothing to do.
      return "noop";
    }
    // Capture the existing vertex range so we can copy it across.
    // Codex P4 — Float32Array.slice returns a Float32Array; preserve
    // typed-array semantics across the copy.
    const existingVertFloats = slot.vertexCount * 3;
    const existingVerts = existingBucket.vertices.slice(
      slot.vertexStart,
      slot.vertexStart + existingVertFloats,
    );
    // Zero-fill the old slot (mirrors `removeEntity` semantics).
    for (
      let i = slot.vertexStart;
      i < slot.vertexStart + existingVertFloats;
      i++
    ) {
      existingBucket.vertices[i] = 0;
    }
    this.entityToSlot.delete(id);
    // Acquire (or split) the destination bucket and push the cached
    // vertices verbatim. This bypasses geometryToLineVertices entirely.
    const destBucket = this.acquireBucket(newBucketKey, slot.vertexCount);
    const newVertexStart = destBucket.vertexLength;
    const newInstanceIndex = destBucket.instanceCount;
    // Codex P4 — grow + bulk-set via Float32Array.set, faster than
    // per-element push.
    ensureBucketCapacity(destBucket, existingVertFloats, slot.vertexCount);
    destBucket.vertices.set(existingVerts, newVertexStart);
    destBucket.vertexLength += existingVertFloats;
    // PR-LT3 variant A (A2) — the rebucketed entity keeps its identity, so its
    // CELTSCALE reciprocal travels with it onto the destination bucket's
    // per-vertex `instanceLtScales` (mirrors the `instanceSlotIndices` fill).
    const ltReciprocal = ltScaleReciprocal(next.linetypeScale);
    const isPaper = spaceIsPaperFlag(next.spaceId);
    for (let i = 0; i < slot.vertexCount; i++) {
      destBucket.instanceSlotIndices[destBucket.instanceSlotLength + i] =
        newInstanceIndex;
      destBucket.instanceLtScales[destBucket.instanceLtScaleLength + i] =
        ltReciprocal;
      destBucket.instanceSpaceIsPaper[
        destBucket.instanceSpaceIsPaperLength + i
      ] = isPaper;
    }
    destBucket.instanceSlotLength += slot.vertexCount;
    destBucket.instanceLtScaleLength += slot.vertexCount;
    destBucket.instanceSpaceIsPaperLength += slot.vertexCount;
    // Segment count: existing slot contributed `vertexCount / 2` line
    // pairs (LINES topology). Preserve that on the destination.
    destBucket.segmentCount += slot.vertexCount / 2;
    destBucket.instanceCount += 1;
    this.entityToSlot.set(id, {
      meshKey: destBucket.keyId,
      vertexStart: newVertexStart,
      vertexCount: slot.vertexCount,
      instanceIndex: newInstanceIndex,
    });
    return "rebucketed";
  }

  getBuckets(): ReadonlyArray<MeshBucket> {
    return [...this.buckets.values()];
  }

  getSlot(id: string): EntitySlot | undefined {
    return this.entityToSlot.get(id);
  }

  size(): number {
    return this.entityToSlot.size;
  }

  clear(): void {
    this.buckets.clear();
    this.entityToSlot.clear();
    this.strata = SINGLE_DRAW_ORDER_STRATUM;
    // F9 (codex P2 #10, post-cycle-1) — drop the shared tessellation
    // cache when a BatchedLayer is wired to it. Without this, every
    // document switch (clear+repopulate) leaked the previous document's
    // Float32Array buffers — the cache lives at module scope and the
    // removeEntity path only invalidates ids it knows about, but
    // `clear()` empties `entityToSlot` without walking it. The cache is
    // module-shared so we only flush when a resolver was wired (= the
    // production code path using the cache); legacy tests that omit the
    // resolver leave the cache untouched.
    if (this.resolveGeomVersion) clearTessellation();
  }

  // ── Internal: the actual vertex packing ───────────────────────────

  /**
   * Phase 2 Wave 2 Task 3 — tessellate an entity's geometry, routing
   * the call through `tessellationCache` when a `resolveGeomVersion`
   * was wired at construction time.
   *
   * Cache lookups key on `(entity.id, geomRev)`. Style-only mutations
   * don't bump `geomRev`, so the cached buffer is reused; geometry
   * mutations bump it and force a re-tessellation.
   *
   * Returns the same `number[]` shape as `geometryToLineVertices` to
   * keep the bucket-packing code identical regardless of the cache
   * path. A future memory pass can switch the bucket vertex buffer
   * itself to Float32 to avoid the boxing round-trip.
   */
  private tessellateEntity(entity: RenderableEntity): number[] {
    // An OUTLINED hatch (`$FILLMODE` 0) is the one stream whose shape is not a
    // function of the geometry alone — it depends on the DOCUMENT variable,
    // which the `(entity.id, geomRev)` cache key cannot see. It is computed
    // ahead of the cache and never stored in it, so a drawing that flips
    // `$FILLMODE` can never be served a stale fill/outline decision; the cost
    // is a copy of the boundary loops, paid only by a FILLMODE-0 drawing's
    // hatches. `null` for every other entity, which is the whole drawing in
    // the default state — one field read, no allocation.
    const hatchOutline = hatchOutlineLineVertices(entity);
    if (hatchOutline) return hatchOutline;
    if (!this.resolveGeomVersion) {
      return geometryToLineVertices(entity.geometry);
    }
    const version = this.resolveGeomVersion(entity.id);
    const buffer = getOrTessellate(entity.id, version, () => {
      const arr = geometryToLineVertices(entity.geometry);
      return Float32Array.from(arr);
    });
    // Convert back to number[] for the existing bucket packer. Same
    // floats, just unboxed back. The cache holds the Float32Array as
    // the canonical form so identity comparisons across calls remain
    // stable.
    const out: number[] = new Array(buffer.length);
    for (let i = 0; i < buffer.length; i++) out[i] = buffer[i] ?? 0;
    return out;
  }

  /**
   * Phase 2 Wave 2 Task 3 — drop the cached tessellation for a single
   * entity. Exposed so external subscribers (entityChanged `removed`
   * handler) can keep the cache map's footprint bounded as entities
   * are deleted.
   */
  invalidateTessellationForEntity(entityId: string): void {
    invalidateTessellation(entityId);
  }

  private insertEntityVertices(entity: RenderableEntity): void {
    const verts = this.tessellateEntity(entity);
    if (verts.length === 0) return;

    const bucket = this.acquireBucket(
      bucketKeyFor(entity, this.strata.stratumOf(entity)),
      verts.length / 3,
    );
    const vertexStart = bucket.vertexLength;
    // Q18.B: every entity gets a unique per-bucket ordinal. Selection
    // state texture indexes by this ordinal.
    const instanceIndex = bucket.instanceCount;
    const vertexCount = verts.length / 3;

    // Codex P4 — grow typed-array buffers if the entity's payload
    // would overrun current capacity. ensureBucketCapacity doubles
    // (or jumps to the required size) and zero-fills the new tail.
    ensureBucketCapacity(bucket, verts.length, vertexCount);
    for (let i = 0; i < verts.length; i++) {
      bucket.vertices[bucket.vertexLength + i] = verts[i] ?? 0;
    }
    bucket.vertexLength += verts.length;
    // PR-LT3 variant A (A2) — per-entity CELTSCALE reciprocal, one value per
    // vertex (same cardinality as `instanceSlotIndices`).
    const ltReciprocal = ltScaleReciprocal(entity.linetypeScale);
    const isPaper = spaceIsPaperFlag(entity.spaceId);
    for (let i = 0; i < vertexCount; i++) {
      bucket.instanceSlotIndices[bucket.instanceSlotLength + i] = instanceIndex;
      bucket.instanceLtScales[bucket.instanceLtScaleLength + i] = ltReciprocal;
      bucket.instanceSpaceIsPaper[bucket.instanceSpaceIsPaperLength + i] =
        isPaper;
    }
    bucket.instanceSlotLength += vertexCount;
    bucket.instanceLtScaleLength += vertexCount;
    bucket.instanceSpaceIsPaperLength += vertexCount;
    bucket.segmentCount += verts.length / 6;
    bucket.instanceCount += 1;

    this.entityToSlot.set(entity.id, {
      meshKey: bucket.keyId,
      vertexStart,
      vertexCount,
      instanceIndex,
    });
  }

  /**
   * Acquire (or create + split) the bucket whose key matches and that
   * still has room for `addedVertexCount` vertices.
   */
  private acquireBucket(key: MeshKey, addedVertexCount: number): MeshBucket {
    const baseKeyId = meshKeyToId(key);
    let bucket = this.buckets.get(baseKeyId);
    if (!bucket) {
      bucket = {
        key,
        keyId: baseKeyId,
        vertices: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        vertexLength: 0,
        segmentCount: 0,
        instanceSlotIndices: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceSlotLength: 0,
        instanceLtScales: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceLtScaleLength: 0,
        instanceSpaceIsPaper: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceSpaceIsPaperLength: 0,
        instanceCount: 0,
      };
      this.buckets.set(baseKeyId, bucket);
      return bucket;
    }
    // Split if the existing bucket would exceed the cap with the new
    // entity. We pick a split-key suffix that grows monotonically so
    // every (layer × color × linetype) family can have many splits.
    const wouldHaveSegments =
      bucket.segmentCount + Math.ceil(addedVertexCount / 2);
    if (wouldHaveSegments > this.segmentCap) {
      let suffix = 1;
      let splitId = `${baseKeyId}#${suffix}`;
      while (this.buckets.has(splitId)) {
        suffix++;
        splitId = `${baseKeyId}#${suffix}`;
      }
      const splitBucket: MeshBucket = {
        key,
        keyId: splitId,
        vertices: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        vertexLength: 0,
        segmentCount: 0,
        splitFromKeyId: baseKeyId,
        instanceSlotIndices: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceSlotLength: 0,
        instanceLtScales: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceLtScaleLength: 0,
        instanceSpaceIsPaper: new Float32Array(BUCKET_INITIAL_FLOAT_CAPACITY),
        instanceSpaceIsPaperLength: 0,
        instanceCount: 0,
      };
      this.buckets.set(splitId, splitBucket);
      return splitBucket;
    }
    return bucket;
  }
}

/**
 * Convert a `SceneGeometry` payload into a flat `LINES`-mode vertex
 * stream (pairs of vertices = one segment). `polyline` strips become
 * (n-1) segment pairs by duplicating intermediate vertices — exactly
 * matches Three.js `LineSegments` topology.
 *
 * Arc / circle / ellipse / spline geometry is NOT pre-tessellated here
 * — the renderer (PR-B+) decides the segment count per camera zoom.
 * For PR-B's bulk-load path we fall back to a coarse
 * `BASELINE_TESS_COUNT` so non-line entities still appear; LOD refresh
 * lands alongside the camera plumbing in a follow-up.
 */
const BASELINE_TESS_COUNT = BAND_ARC_SAMPLES_PER_TURN;

/**
 * Sub-segments per Catmull-Rom span when tessellating a `leaderType = 2`
 * (Spline) MULTILEADER leader line. 16 keeps the curve visually smooth for
 * the short winding leaders MLEADER produces while staying cheap.
 */
const MULTILEADER_SPLINE_SEGMENTS_PER_SPAN = 16;

export function geometryToLineVertices(geometry: SceneGeometry): number[] {
  switch (geometry.kind) {
    case "line": {
      return [
        geometry.a.x,
        geometry.a.y,
        geometry.a.z,
        geometry.b.x,
        geometry.b.y,
        geometry.b.z,
      ];
    }
    case "polyline": {
      // A polyline carrying authored WIDTHS (DXF groups 40/41, or the
      // entity-level default/constant width the adapter resolved into them) is
      // a BAND, not a centreline — see `widePolylineLineVertices`. Checked
      // first and bailing out on the very first field read, so the
      // overwhelmingly common width-less polyline pays nothing.
      //
      // Either stream is then swept along the entity's group-39 THICKNESS
      // (@codex PR #785 round 15, FINDING 3) — the surface `cad-core`'s
      // `heavyPolylineDrawnBounds` has indexed since round 9 and the canvas did
      // not paint. `withPolylineThicknessSweep` returns the stream ITSELF when
      // there is no thickness, which is every polyline but a handful, and its
      // header carries the AutoCAD wireframe rule for the rulings (hence the
      // two different anchor sets below: the BAND's own silhouette vertices,
      // versus the width-less run's AUTHORED vertices — the centreline is
      // inside the swept solid, so its rulings are not surface edges).
      const band = widePolylineLineVertices(geometry);
      if (band) return withPolylineThicknessSweep(geometry, band, null);
      // A BULGED span is the circular ARC it encodes, and that arc lives in the
      // entity's OWN OCS plane — `geometry.extrusion` — not in the WCS XY the
      // lifted points happen to project onto (see
      // `adapters/geom/polylineWidthBand.ts::polylineCentrelineSamples`; the
      // width BAND two functions down has been built that way since round 5,
      // and this is the same sentence for the centreline).
      return withPolylineThicknessSweep(
        geometry,
        polylineToLineVertices(
          polylineCentrelineSamples(
            geometry.points,
            geometry.closed,
            geometry.bulges,
            geometry.extrusion,
          ),
          geometry.closed,
        ),
        geometry.points,
      );
    }
    case "mesh-wire": {
      // A polygon / poly-face mesh wireframe is several DISJOINT runs of ONE
      // entity, so each run contributes its own strokes to the same bucket —
      // never a link from the end of one run to the start of the next (that
      // zig-zag is exactly what walking the raw VERTEX order used to draw).
      const out: number[] = [];
      for (const run of geometry.runs) {
        const verts = polylineToLineVertices(run.points, run.closed);
        for (const v of verts) out.push(v);
      }
      return out;
    }
    case "circle": {
      const samples = sampleCircle(
        geometry.center,
        geometry.radius,
        BASELINE_TESS_COUNT,
      );
      return polylineToLineVertices(samples, true);
    }
    case "arc": {
      const samples = sampleArc(
        geometry.center,
        geometry.radius,
        geometry.start,
        geometry.end,
        BASELINE_TESS_COUNT,
      );
      return polylineToLineVertices(samples, false);
    }
    case "ellipse": {
      // SL-15 F-C — sample the ellipse in its TRUE 3D plane: the shared
      // `tessellateEllipse` computes the major length + orientation from the full
      // 3D `majorAxis` and places the minor axis via the OCS `extrusion` normal
      // (absent ⇒ (0,0,1)). A tilted major axis now renders tilted instead of flat.
      const samples = tessellateEllipse(
        geometry.center,
        geometry.majorAxis,
        geometry.ratio,
        geometry.start,
        geometry.end,
        BASELINE_TESS_COUNT,
        geometry.extrusion,
      );
      // Closed when full ellipse, else open arc.
      const closed =
        Math.abs(geometry.end - geometry.start) >= Math.PI * 2 - 1e-9;
      return polylineToLineVertices(samples, closed);
    }
    case "spline": {
      // Coarse polyline through control points; PR-C upgrades to a real
      // de-Boor / Bezier sampler. Sufficient for bbox + initial render.
      return polylineToLineVertices(geometry.controlPoints, false);
    }
    case "dimension": {
      // PR-F P1 — flatten DimensionPrimitive[] into line-pair
      // vertices for the line-class primitives (extension-line,
      // dim-line, leader, arc). Arrow / tick / text primitives are
      // handled out-of-band by the dimension mesh factories and
      // synthesised text-renderables — they produce their own
      // THREE objects, not line-bucket vertices.
      const out: number[] = [];
      for (const prim of geometry.primitives) {
        switch (prim.kind) {
          case "extension-line":
          case "dim-line":
          case "leader":
            out.push(
              prim.a.x,
              prim.a.y,
              prim.a.z,
              prim.b.x,
              prim.b.y,
              prim.b.z,
            );
            break;
          case "arc": {
            const samples = sampleArc(
              prim.center,
              prim.radius,
              prim.startAngle,
              prim.endAngle,
              BASELINE_TESS_COUNT,
            );
            const verts = polylineToLineVertices(samples, false);
            for (const v of verts) out.push(v);
            break;
          }
          case "elliptic-arc": {
            // Codex PR #784 round 12 P2 — the affine image of an `arc` under a
            // NON-UNIFORM block INSERT is an ellipse arc, not a circle at
            // `radius · |scaleX|` (`adapters/typed/blockTransform.ts
            // ::transformDimensionEllipseArc`). Sampled by the SAME shared
            // `tessellateEllipse` the `ellipse` geometry kind above uses — one
            // ellipse sampler in this batcher, not two — with its implicit
            // (0,0,1) normal, which is the plane every dimension primitive
            // lives in.
            const samples = tessellateEllipse(
              prim.center,
              prim.majorAxis,
              prim.ratio,
              prim.startParam,
              prim.endParam,
              BASELINE_TESS_COUNT,
            );
            const verts = polylineToLineVertices(samples, false);
            for (const v of verts) out.push(v);
            break;
          }
          case "arrow":
          case "tick":
          case "text":
            // Handled by the dimension mesh factory + synthesised
            // text-renderable pipeline (PR-F P1) — no line-bucket
            // contribution.
            break;
        }
      }
      return out;
    }
    case "multileader": {
      // S9b — flatten the leader-line / dogleg / landing primitives into
      // the gl.LINES vertex stream. The `arrow` heads go out-of-band
      // through `buildMultileaderArrowMeshes` (InstancedMesh / LineSegments,
      // mirroring the dimension arrow factory), `mtext-content` through the
      // synthesised MTEXT text pipeline (`synthesizeMultileaderTextRenderables`),
      // and `block-content` is expanded into first-class child renderables at
      // bulk-load (identical to a top-level INSERT walk — see the
      // `block-content.flattened` contract in `types.ts` and
      // `finishSceneBulkLoad`), so none of those three contribute here.
      const out: number[] = [];
      for (const prim of geometry.primitives) {
        switch (prim.kind) {
          case "leader-line": {
            // `spline` leaders draw a Catmull-Rom cubic Bézier through the
            // vertices (CLAUDE.md ПРИОРИТЕТ #1 — cubic, never quadratic);
            // straight leaders emit the vertices verbatim.
            const pts: Vec3[] = prim.spline
              ? sampleCatmullRomCubic(
                  prim.vertices,
                  MULTILEADER_SPLINE_SEGMENTS_PER_SPAN,
                ).map((p) => ({ x: p.x, y: p.y, z: 0 }))
              : prim.vertices;
            const verts = polylineToLineVertices(pts, false);
            for (const v of verts) out.push(v);
            break;
          }
          case "dogleg":
          case "landing":
            out.push(
              prim.a.x,
              prim.a.y,
              prim.a.z,
              prim.b.x,
              prim.b.y,
              prim.b.z,
            );
            break;
          case "arrow":
          case "block-content":
          case "mtext-content":
            // Out-of-band (see the case header) — no line-bucket contribution.
            break;
        }
      }
      return out;
    }
    case "point":
    case "text":
    case "mtext":
    case "hatch":
    case "wipeout":
    case "viewport":
    case "block-insert":
    case "array-rect":
    default:
      // PR-D / PR-E / PR-G handle these — adapter still emits the
      // RenderableEntity shape so the spatial index works, but the
      // bucket packer skips them. Returning [] keeps `addEntity` a
      // no-op for these kinds.
      //
      // `hatch` stays here in BOTH fill states: this function sees the
      // GEOMETRY alone, and whether a hatch strokes its boundary is a
      // DOCUMENT answer (`$FILLMODE`). `hatchOutlineLineVertices` — which
      // takes the whole entity — owns that stream, and `tessellateEntity`
      // consults it before reaching here.
      return [];
  }
}

/**
 * A width-carrying polyline as the gl.LINES stream of its BAND SILHOUETTE, or
 * `null` when the geometry declares no width (the caller then strokes the
 * centreline).
 *
 * # Why the silhouette is stroked in BOTH fill states
 *
 * `FILLMODE` decides whether the band's INTERIOR is painted (Autodesk
 * system-variable reference: "Specifies whether hatches and fills, 2D solids,
 * and wide polylines are filled in", INITIAL VALUE 1). At 1 the interior is
 * covered by the triangulated mesh (`scene/widePolylineFillMesh.ts`); at 0 it
 * is not. Either way the BOUNDARY is real geometry the drawing contains, so
 * this function emits it unconditionally and takes no `fillmode` argument —
 * which is why every caller's call site is byte-identical to what it was
 * before the fill existed.
 *
 * Two properties of the renderer make the outline load-bearing rather than
 * decorative, and both are lost if the band is mesh-only:
 *
 *   - SELECTION. The per-instance selected / hovered / preview byte lives on
 *     the LINE buckets (`rendering/selectionStateTexture.ts` +
 *     `patchMaterialWithSelectionState`), addressed by the slot
 *     `insertEntityVertices` allocates. An entity with no line slot gets no
 *     slot, so it can never light up — a wide polyline would lose the
 *     highlight it has always had (CLAUDE.md ПРИОРИТЕТ #1: a render
 *     improvement may not trade away existing parity).
 *   - MINIMUM ON-SCREEN WIDTH. A fill is triangles, and triangle
 *     rasterisation has no minimum-width floor: a band narrower than a pixel
 *     — group 43 = 0.15 is the width most of this repo's own fixtures carry —
 *     can thin out or drop entirely when zoomed out. The outline is stroked
 *     at the entity's lineweight and is therefore always at least a pixel, so
 *     the entity stays visible at any zoom, which is what AutoCAD shows at
 *     small scale.
 *
 * The band's own fill mesh is drawn UNDER the line buckets
 * (`WIDE_POLYLINE_FILL_RENDER_ORDER`), so the two never fight: the outline —
 * and its selection colour — is never painted over by the interior. The
 * overshoot the outline adds is half a lineweight on each edge, sub-pixel at
 * the default; the vector PLOT, which has no selection and no pixel floor to
 * worry about, stays fill-only (`print/pdf/converters/widePolylineBand.ts`).
 *
 * Nothing about WHERE the band goes is decided here. The width a span is drawn
 * at (`widths[i]` describes the span from vertex `i` to vertex `i + 1`, which
 * is why it is per-VERTEX and not per-span — Autodesk DXF reference), the
 * bulged-span subdivision and the taper carried across it are
 * `adapters/geom/polylineWidthBand.ts::widePolylineBandLoops`, the SAME call
 * the vector-PDF plot makes — including WHICH FRAME the band is offset in,
 * which for a tilted-OCS entity is not the WCS XY plane its lifted points sit
 * in (`geometry.extrusion`) and for a block child under a non-similarity
 * INSERT is not even a rigid plane (`geometry.bandFrame`, which supersedes it);
 * see `adapters/geom/polylinePlane.ts`. The gate and that call are now ONE
 * function, `adapters/geom/polylineDrawnBand.ts::drawnPolylineBandLoops`,
 * shared with the pointer pick and window select so no consumer can test a
 * silhouette the canvas is not drawing.
 */
function widePolylineLineVertices(
  geometry: DrawnBandGeometry,
): number[] | null {
  const loops = drawnPolylineBandLoops(geometry);
  if (!loops) return null;
  const out: number[] = [];
  for (const loop of loops) {
    for (const v of polylineToLineVertices(loop, true)) out.push(v);
  }
  return out;
}

/**
 * A HATCH as the gl.LINES stream of its BOUNDARY, or `null` when this entity is
 * not an outlined hatch — i.e. for everything that is not a HATCH, and for a
 * HATCH in a drawing whose `$FILLMODE` paints (1, and the absent value that
 * means 1).
 *
 * # Why a hatch has a line stream at all
 *
 * `geometryToLineVertices` returns `[]` for `kind: "hatch"`, because at
 * FILLMODE 1 a hatch is ONLY its fill: AutoCAD fills a solid hatch without
 * stroking the boundary (the boundary polyline, where one is drawn, is a
 * separate entity), and a patterned one is its pattern lines. At FILLMODE 0
 * there is no fill of any tier —
 * `scene/sceneMeshFactories.ts::buildHatchFillMesh` returns `null` for solid,
 * gradient and pattern alike — and the boundary becomes the whole entity:
 *
 *   Autodesk, "To turn on or off the display of solid fills": "When you turn
 *   off Fill mode, wide polylines, solid-filled polygons, gradient fill, and
 *   hatches are displayed in outline form." FILL (Command), Off: "Only the
 *   outlines of objects are displayed and plotted."
 *
 *   `ezdxf` `addons/drawing/frontend.py::draw_hatch_entity` under
 *   `HatchPolicy.SHOW_OUTLINE` ("show only the outline of HATCH entities")
 *   hands the SAME boundary paths it would have filled to `draw_path` —
 *   stroked, not filled — having first replaced `filling` so the PATTERN
 *   branch is bypassed too.
 *
 * So the zero state is a stroke over the very loops the fill would have
 * covered. Emitting them into the LINE buckets (rather than as a second direct
 * mesh) is what gives an outlined hatch the entity's resolved colour,
 * lineweight and linetype, and the per-instance SELECTION byte that lives on a
 * line-bucket slot — a hatch that lost its fill mesh and gained nothing would
 * be invisible AND unhighlightable.
 *
 * `loops` are the RENDERED boundary loops (the hatch-style walk in
 * `adapters/geom/hatchGeometry.ts` already applied `renderedBoundaryLoopIndices`
 * and pre-sampled every arc / elliptic-arc edge to 33 points), which is the
 * same set `buildHatchFillMesh` earcuts — so screen fill and screen outline
 * cannot disagree about WHICH boundary the hatch has. Interior loops are
 * stroked too: a hole's edge is ink in outline form.
 *
 * Reads `RenderableEntity.fillmode` through {@link fillModePaintsSolid}, the
 * one predicate the band fill, the draw-order plan, the pick, window select and
 * the PLOT share (@codex PR #785 round 16, P2).
 */
export function hatchOutlineLineVertices(
  entity: RenderableEntity,
): number[] | null {
  if (entity.geometry.kind !== "hatch") return null;
  if (fillModePaintsSolid(entity.fillmode)) return null;
  const out: number[] = [];
  for (const loop of entity.geometry.loops) {
    for (const v of polylineToLineVertices(loop.vertices, loop.closed)) {
      out.push(v);
    }
  }
  return out;
}

/** Convert a list of `n` vertices into `2(n-1)` line-pair vertices. */
function polylineToLineVertices(
  points: readonly Vec3[],
  closed: boolean,
): number[] {
  if (points.length < 2) return [];
  const out: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i];
    const b = points[i + 1];
    if (!a || !b) continue;
    out.push(a.x, a.y, a.z, b.x, b.y, b.z);
  }
  if (closed) {
    const last = points[points.length - 1];
    const first = points[0];
    if (last && first)
      out.push(last.x, last.y, last.z, first.x, first.y, first.z);
  }
  return out;
}

/** One point on a circle of `radius` about `center` at `angle` (radians), keeping
 * the center's elevation `z`. Single truth shared by the circle + arc samplers. */
function circlePoint(center: Vec3, radius: number, angle: number): Vec3 {
  return {
    x: center.x + radius * Math.cos(angle),
    y: center.y + radius * Math.sin(angle),
    z: center.z,
  };
}

function sampleCircle(center: Vec3, radius: number, count: number): Vec3[] {
  const out: Vec3[] = [];
  for (let i = 0; i < count; i++) {
    out.push(circlePoint(center, radius, (i / count) * Math.PI * 2));
  }
  return out;
}

function sampleArc(
  center: Vec3,
  radius: number,
  startRad: number,
  endRad: number,
  count: number,
): Vec3[] {
  let sweep = endRad - startRad;
  if (sweep < 0) sweep += Math.PI * 2;
  const out: Vec3[] = [];
  for (let i = 0; i <= count; i++) {
    out.push(circlePoint(center, radius, startRad + sweep * (i / count)));
  }
  return out;
}

/**
 * Look up the entity's linetype from its layer / property bag.
 * Currently we only carry `colorHex` and `lineweightWorld` on the
 * `RenderableEntity` — linetype name is recovered via a side channel
 * in PR-B+. For now we default to "Continuous" so the bucket key still
 * works.
 */
export function extractLinetype(entity: RenderableEntity): string {
  // PR-LT3 — the adapter (`adapterContext.buildRenderableShell`) now
  // propagates the DXF group-6 linetype NAME onto `RenderableEntity.linetype`.
  // Carry that name verbatim into the bucket key. `ByLayer` / `ByBlock`
  // sentinels are kept as-is here (bucket grouping reflects what the entity
  // declared); the material-resolve step maps a sentinel to its concrete
  // pattern. An empty name falls back to `"ByLayer"` to mirror the adapter's
  // own default (AutoCAD CELTYPE default).
  const name = entity.linetype?.trim();
  return name && name.length > 0 ? name : "ByLayer";
}
